import React, { useEffect, useState } from "react";
import { Download, FileText, Loader2 } from "lucide-react";

import { toast } from "../../../utils/appToast";
import { fetchRfpInvoicePdfUrl, fetchRfpInvoices, formatDate, formatMoney } from "../../../services/rfpBillingApi";

const KIND_LABEL = {
  purchase: "RFP Manager — Premium",
  renewal: "RFP Manager — renewal",
  seat_increase: "RFP Manager — additional seats",
};

export default function BillingHistoryTable({ billingEmail }) {
  const [invoices, setInvoices] = useState(null);
  const [downloading, setDownloading] = useState(null); // invoice number being fetched

  const download = async (number) => {
    setDownloading(number);
    try {
      // Stripe serves it as a file, so this downloads without leaving the page.
      window.location.assign(await fetchRfpInvoicePdfUrl(number));
    } catch (err) {
      toast.error(err?.response?.data?.error || "Couldn't download this invoice — try again.");
    } finally {
      setDownloading(null);
    }
  };

  useEffect(() => {
    let cancelled = false;
    fetchRfpInvoices()
      .then((data) => {
        if (!cancelled) setInvoices(data.invoices || []);
      })
      .catch(() => {
        if (!cancelled) setInvoices([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="rounded-[14px] border border-[#ECE9EE] bg-white p-5">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="text-[16px] font-semibold text-[#1A1A1A]">Billing history</h3>
          <p className="text-[12.5px] text-[#8A8A90]">
            {billingEmail ? `Invoices are emailed to ${billingEmail} after each payment.` : "Invoices appear here after each payment."}
          </p>
        </div>
      </div>

      {invoices === null ? (
        <div className="space-y-2">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-10 animate-pulse rounded-lg bg-[#FAF9FB]" />
          ))}
        </div>
      ) : invoices.length === 0 ? (
        <div className="flex flex-col items-center gap-2 py-10 text-center">
          <div className="flex h-11 w-11 items-center justify-center rounded-[10px] bg-[#FAF9FB] text-[#8A8A90]">
            <FileText size={18} />
          </div>
          <p className="text-[14px] font-medium text-[#1A1A1A]">No invoices yet</p>
          <p className="text-[12.5px] text-[#8A8A90]">Your first invoice will appear here after your first payment.</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[420px] border-collapse text-[13.5px]">
            <thead>
              <tr className="bg-[#FAF9FB] text-left text-[11px] uppercase tracking-[.03em] text-[#8A8A90]">
                <th className="px-3 py-2 font-semibold">Invoice · Date</th>
                <th className="px-3 py-2 font-semibold">Description</th>
                <th className="px-3 py-2 text-right font-semibold">Amount</th>
                <th className="px-3 py-2 font-semibold">Status</th>
                <th className="px-3 py-2 font-semibold">Download</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((inv) => (
                <tr key={inv.number} className="border-t border-[#ECE9EE]">
                  <td className="px-3 py-3">
                    <div className="font-semibold text-[#1A1A1A]">{inv.number}</div>
                    <div className="text-[11.5px] text-[#8A8A90]">{formatDate(inv.created_at)}</div>
                  </td>
                  <td className="px-3 py-3 text-[#52525B]">{KIND_LABEL[inv.kind] || inv.kind}</td>
                  <td className="px-3 py-3 text-right font-semibold text-[#1A1A1A] [font-variant-numeric:tabular-nums]">
                    {formatMoney(inv.total, inv.currency, false)}
                  </td>
                  <td className="px-3 py-3">
                    {inv.status === "paid" ? (
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-[#EEF9F0] px-2.5 py-1 text-[12px] font-medium text-[#2F7D44]">
                        <span className="h-1.5 w-1.5 rounded-full bg-[#2F7D44]" /> Paid
                      </span>
                    ) : (
                      <span className="rounded-full bg-[#FBF5FA] px-2.5 py-1 text-[12px] font-medium text-[#893F82]">
                        {inv.status}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-3">
                    <button
                      type="button"
                      onClick={() => download(inv.number)}
                      disabled={downloading !== null}
                      aria-label={`Download invoice ${inv.number}`}
                      title="Download PDF"
                      className="flex h-8 w-8 items-center justify-center rounded-[8px] border border-[#ECE9EE] text-[#52525B] hover:bg-[#FBF5FA] hover:text-[#A04A97] disabled:opacity-50"
                    >
                      {downloading === inv.number ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
